import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  PackageTierSelect, PackageTierOptionsSelect,
  getPackageTier, getPackagePlatform, getPackageCreditRate, getPackageCredits,
} from "../../utils/packages";
import { Money, Username } from "../../utils/privacyMask";
import "./QuickActionModal.css";

const DAY_MS = 86400000;
const addDays   = (ms, days)   => ms + days * DAY_MS;
const addMonths = (ms, months) => { const d = new Date(ms); d.setMonth(d.getMonth() + months); return d.getTime(); };

const EXTEND_OPTIONS = [
  { label: "+7 days",   apply: ms => addDays(ms, 7) },
  { label: "+1 month",  apply: ms => addMonths(ms, 1) },
  { label: "+3 months", apply: ms => addMonths(ms, 3) },
  { label: "+6 months", apply: ms => addMonths(ms, 6) },
  { label: "+12 months", apply: ms => addMonths(ms, 12) },
];

const TITLES = {
  renew:   "Renew Subscription",
  pause:   "Pause Subscription",
  cancel:  "Cancel Subscription",
  extend:  "Extend Subscription",
  upgrade: "Change Package",
};

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const API = () => process.env.REACT_APP_API_URL;

export default function QuickActionModal({ mode, subscription, onClose, onSuccess }) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // ── Extend state ──
  const [customDate, setCustomDate] = useState("");
  const [pendingExpireDate, setPendingExpireDate] = useState(null);

  // ── Upgrade/transfer state ──
  const [packageTier, setPackageTier] = useState(() => getPackageTier(subscription.package));
  const [newPackage, setNewPackage] = useState("");
  const [fromCredits, setFromCredits] = useState("");
  const [toCredits, setToCredits] = useState("");

  const oldPlatform = getPackagePlatform(subscription.package);
  const oldTier = getPackageTier(subscription.package);
  const oldCreditRate = getPackageCreditRate(oldTier);
  const newPlatform = newPackage ? getPackagePlatform(Number(newPackage)) : null;
  const isTransfer = newPackage && newPlatform !== oldPlatform;
  const newCreditRate = packageTier ? getPackageCreditRate(packageTier) : null;

  const fromValueEUR = isTransfer && fromCredits !== "" && oldCreditRate != null ? Number(fromCredits) * oldCreditRate : null;
  const toValueEUR   = isTransfer && toCredits !== "" && newCreditRate != null ? Number(toCredits) * newCreditRate : null;
  const netDeltaEUR  = fromValueEUR != null && toValueEUR != null ? fromValueEUR - toValueEUR : null;

  const handlePackageTierChange = (tier) => {
    setPackageTier(tier);
    setNewPackage("");
    setToCredits("");
  };

  const handlePackageChange = (id) => {
    setNewPackage(id);
    setToCredits(id ? String(getPackageCredits(Number(id)) ?? "") : "");
  };

  const putSubscription = async (overrides) => {
    await axios.put(
      `${API()}/subscription/update`,
      { ...subscription, ...overrides },
      { headers: headers() }
    );
  };

  const handleRenew = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await putSubscription({ active: false });
      navigate('/subscription/create', {
        state: {
          accountID: subscription.accountID,
          username: subscription.username,
          password: subscription.password,
          package: subscription.package,
          notes: subscription.notes,
          additionalAccounts: subscription.additionalAccounts,
        },
      });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to renew subscription");
      setSubmitting(false);
    }
  };

  const handlePauseOrCancel = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await putSubscription({ active: false });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update subscription");
      setSubmitting(false);
    }
  };

  const handleExtend = async () => {
    if (!pendingExpireDate) { setError("Pick a new expire date"); return; }
    setSubmitting(true);
    setError(null);
    try {
      await putSubscription({ expireDate: pendingExpireDate + "" });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to extend subscription");
      setSubmitting(false);
    }
  };

  const handleUpgrade = async () => {
    if (!newPackage) { setError("Select a new package"); return; }
    if (isTransfer && (fromCredits === "" || toCredits === "")) {
      setError("Enter both credits refunded and credits spent to log the transfer");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await putSubscription({ package: Number(newPackage) });
      if (isTransfer) {
        await axios.post(
          `${API()}/transaction/create`,
          {
            accountID: subscription.accountID,
            type: 'expense',
            category: 'platform-transfer',
            revenue: 0,
            profit: netDeltaEUR,
            transactionDate: Date.now() + "",
            notes: `Transfer: ${oldTier} → ${packageTier}`,
            transferDetails: {
              fromPlatform: oldPlatform, fromPackage: subscription.package, fromCredits: Number(fromCredits), fromCreditRate: oldCreditRate, fromValueEUR,
              toPlatform: newPlatform, toPackage: Number(newPackage), toCredits: Number(toCredits), toCreditRate: newCreditRate, toValueEUR,
              netDeltaEUR,
            },
          },
          { headers: headers() }
        );
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to change package");
      setSubmitting(false);
    }
  };

  const confirmHandlers = { renew: handleRenew, pause: handlePauseOrCancel, cancel: handlePauseOrCancel, extend: handleExtend, upgrade: handleUpgrade };
  const confirmDisabled = submitting || (mode === 'extend' && !pendingExpireDate) || (mode === 'upgrade' && !newPackage);

  const modal = (
    <div className="qam-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="qam-panel">
        <div className="qam-header">
          <span>{TITLES[mode]}</span>
          <button className="qam-close" onClick={onClose} aria-label="Close"><i className="bi bi-x-lg" /></button>
        </div>

        <div className="qam-body">
          {mode === 'renew' && (
            <p className="qam-text">
              This marks the current subscription as inactive and opens a pre-filled renewal form for
              <strong> <Username value={subscription.username || subscription.accountID} /></strong>. Continue?
            </p>
          )}

          {(mode === 'pause' || mode === 'cancel') && (
            <p className="qam-text">
              {mode === 'pause' ? 'Pause' : 'Cancel'} this subscription
              <strong> <Username value={subscription.username || subscription.accountID} /></strong>? It will be marked inactive.
            </p>
          )}

          {mode === 'extend' && (
            <>
              <p className="qam-text">Current expiry: <strong>{new Date(Number(subscription.expireDate)).toLocaleDateString()}</strong></p>
              <div className="qam-chip-row">
                {EXTEND_OPTIONS.map(opt => (
                  <button
                    key={opt.label}
                    type="button"
                    className={`qam-chip${pendingExpireDate === opt.apply(Number(subscription.expireDate)) ? ' active' : ''}`}
                    onClick={() => { setPendingExpireDate(opt.apply(Number(subscription.expireDate))); setCustomDate(''); }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="form-field" style={{ marginTop: 12 }}>
                <label className="form-field-label">Or pick a custom date</label>
                <input
                  className="form-input" type="date" value={customDate}
                  onChange={e => { setCustomDate(e.target.value); setPendingExpireDate(e.target.value ? new Date(e.target.value).getTime() : null); }}
                />
              </div>
              {pendingExpireDate && (
                <p className="qam-text" style={{ marginTop: 10 }}>
                  New expiry: <strong>{new Date(pendingExpireDate).toLocaleDateString()}</strong>
                </p>
              )}
            </>
          )}

          {mode === 'upgrade' && (
            <>
              <div className="form-field">
                <label className="form-field-label">Package Type</label>
                <select className="form-input" value={packageTier} onChange={e => handlePackageTierChange(e.target.value)}>
                  <PackageTierSelect />
                </select>
              </div>
              {packageTier && (
                <div className="form-field" style={{ marginTop: 10 }}>
                  <label className="form-field-label">Package Option</label>
                  <select className="form-input" value={newPackage} onChange={e => handlePackageChange(e.target.value)}>
                    <PackageTierOptionsSelect tier={packageTier} />
                  </select>
                </div>
              )}

              {isTransfer && (
                <div className="qam-transfer">
                  <div className="form-section-label" style={{ marginTop: 14 }}>Log platform transfer</div>
                  <p className="qam-text" style={{ fontSize: '0.8rem' }}>
                    Moving from <strong>{oldTier}</strong> (<Money value={oldCreditRate} />/credit) to <strong>{packageTier}</strong> (<Money value={newCreditRate} />/credit).
                  </p>
                  <div className="row g-3">
                    <div className="col-6">
                      <div className="form-field">
                        <label className="form-field-label">Credits refunded ({oldTier})</label>
                        <input className="form-input" type="number" step="0.01" min="0" value={fromCredits} onChange={e => setFromCredits(e.target.value)} placeholder="e.g. 2.5" />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-field">
                        <label className="form-field-label">Credits spent ({packageTier})</label>
                        <input className="form-input" type="number" step="0.01" min="0" value={toCredits} onChange={e => setToCredits(e.target.value)} />
                      </div>
                    </div>
                  </div>
                  {netDeltaEUR !== null && (
                    <p className={`qam-delta ${netDeltaEUR >= 0 ? 'pos' : 'neg'}`}>
                      <Money value={fromValueEUR} /> refunded − <Money value={toValueEUR} /> spent =
                      <strong> <Money value={netDeltaEUR} /> {netDeltaEUR >= 0 ? 'gain' : 'expense'}</strong>
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {error && <div className="form-banner error" style={{ marginTop: 14 }}><i className="bi bi-exclamation-triangle-fill" />{error}</div>}
        </div>

        <div className="qam-footer">
          <button type="button" className="btn-form-cancel" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-form-submit" disabled={confirmDisabled} onClick={confirmHandlers[mode]}>
            {submitting ? 'Working…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
